// Team roster for the public-facing About page.
// Add real members by replacing entries below; leave slots empty to show
// dashed placeholders ("Reserved for future team member") in the grid.
//
// Each member gets its own page at /about/team/:slug, linked via the member's
// `slug`. Photos can live anywhere reachable from /public (e.g.
// /assets/team/<slug>.webp) or as a remote URL.

export interface TeamMember {
  slug: string;
  name: string;
  role: string;
  /** Optional path/URL. If missing, the card renders the member's initials. */
  photo?: string;
  /** One-line tagline shown on the card. */
  tagline?: string;
  /** Long-form biography paragraphs for the detail page. */
  bio: string[];
  /** Optional highlights / accomplishments list. */
  highlights?: string[];
  /** Optional contact links. */
  email?: string;
  linkedin?: string;
  twitter?: string;
  instagram?: string;
  youtube?: string;
  discord?: string;
  website?: string;
}

/**
 * Real, filled-in team members. Render first (left → right, top → bottom).
 * Order in this array is the order they appear on the About page.
 *
 * Order rationale: Founders → Directors → Specialists → Partners → Interns.
 * Bios are intentionally left empty for now — fill in per-member as we go.
 */
export const team: TeamMember[] = [
  {
    slug: 'anthony-marquez',
    name: 'Anthony Marquez',
    role: 'Co-Founder & Digital Marketing Strategist',
    photo: '/assets/team/anthony-marquez.webp',
    tagline:
      'Houston-based filmmaker, Marine Corps veteran, and nonprofit founder building opportunity through media, storytelling, and community.',
    bio: [
      'Anthony A. Marquez is a Houston-based filmmaker, police officer, Marine Corps veteran, and nonprofit founder. Across law enforcement, logistics, and digital production, he blends technical skill, leadership, and creativity to build opportunities through media, storytelling, and community.',
      'He leads Mystic Media Film, a Texas-licensed, veteran-owned production and marketing company built to be a small business’s full media department in one team. The work spans cinematic video, brand systems, social media strategy, and business-focused content, with engagements ranging from commercial productions to law enforcement media initiatives and creative brand campaigns.',
      'As founder and president of Creator Terminal, an IRS-recognized 501(c)(3) nonprofit, Anthony is building a large-scale creative hub focused on accessible education in film, audio engineering, graphic design, coding, business administration, and 3D development. The vision centers on helping everyday people, from students to small business owners, gain access to tools, training, and creative opportunities that are often out of reach.',
      'In entertainment, he created MyShowBro, a federally trademarked, creator-driven media platform and YouTube network that has been in continuous use since 2013. MyShowBro is home to original storytelling, dark comedy, puppetry, filmmaking, and independent creator collaborations, and it serves as a launchpad for aspiring filmmakers.',
      'Before entering law enforcement and media full time, Anthony served honorably in the United States Marine Corps as a Motor Transport Operator, where he learned convoy operations, logistics, and leadership under pressure. That combination of discipline, operational thinking, and creative ambition still shapes the way he approaches both business and community building today.',
      'At his core, he is driven by a single belief: creativity, education, and storytelling can change the trajectory of people’s lives, especially for those who do not traditionally have access to those opportunities.'
    ],
    highlights: [
      'Honorably discharged United States Marine Corps veteran (Motor Transport Operator)',
      'Independent media production since 2013',
      'Sworn police officer',
      'Federal trademark holder, MYSHOWBRO® (U.S. Reg. No. 6,912,480)',
      'Founder and President, Creator Terminal (IRS-recognized 501(c)(3))',
      'Managing Member, Mystic Media Film LLC (Texas veteran-owned business)'
    ],
    email: 'sales@mysticmediafilm.com',
    instagram: 'https://instagram.com/myshowbro',
    youtube: 'https://youtube.com/@Myshowbro',
    discord: 'https://discord.com/invite/hdVMacjRdB'
  },
  {
    slug: 'saige-santana',
    name: 'Saige Santana',
    role: 'Co-Founder & Creative Director',
    photo: '/assets/team/saige-santana.webp',
    bio: []
  },
  {
    slug: 'zachary-rahimi',
    name: 'Zachary Rahimi',
    role: 'Founder, Relight Studio',
    photo: '/assets/team/zachary-rahimi.webp',
    bio: []
  },
  {
    slug: 'noah-hedegaard',
    name: 'Noah Hedegaard',
    role: 'Founder, Gaard Media',
    photo: '/assets/team/noah-hedegaard.webp',
    bio: []
  },
  {
    slug: 'nathan-kester',
    name: 'Nathan Kester',
    role: 'Gaard Media Intern',
    photo: '/assets/team/nathan-kester.webp',
    bio: []
  },
  {
    slug: 'karla-fernanda',
    name: 'Karla Fernanda',
    role: 'Finance Director',
    photo: '/assets/team/karla-fernanda.webp',
    bio: []
  },
  {
    slug: 'daryl-joy-merencillo',
    name: 'Daryl Joy Merencillo',
    role: 'Lead Graphic Designer',
    photo: '/assets/team/daryl-joy-merencillo.webp',
    bio: []
  },
  {
    slug: 'myke-casen',
    name: 'Myke Casen',
    role: 'Audio Engineer',
    photo: '/assets/team/myke-casen.webp',
    bio: []
  },
  {
    slug: 'eric-crane',
    name: 'Eric Crane',
    role: 'Art Director',
    photo: '/assets/team/eric-crane.webp',
    bio: []
  },
  {
    slug: 'drew-weiser',
    name: 'Drew Weiser',
    role: 'Liaison',
    photo: '/assets/team/drew-weiser.webp',
    bio: []
  },
  {
    slug: 'branten-ogg',
    name: 'Branten Ogg',
    role: 'Director of Community (Intern)',
    photo: '/assets/team/branten-ogg.webp',
    bio: []
  },
  {
    slug: 'stacey-blanco',
    name: 'Stacey Blanco',
    role: 'Visual Storyteller (Intern)',
    photo: '/assets/team/stacey-blanco.webp',
    bio: []
  }
];

/**
 * Total number of cards in the grid (real + placeholders).
 * With 12 real members, this fills the grid evenly at 4 rows × 3 cols on
 * desktop with no reserved tiles. Bump higher to add "Reserved" placeholders
 * when hiring.
 */
export const PLACEHOLDER_SLOTS = 12;
